import { AdminProfessionals } from '@platform/client';
import { useParams } from 'wouter';
import {
  AdminDetailPage,
  SectionedPageActionsProps,
  DetailPageAction,
} from '../../AdminDetailPage';
import { ProfileTabContent } from './ProfileTabContent';
import { ResetUserModal } from '../../components/ResetUserModal';
import { AlertProps } from '@platform/components';
import {
  AccountTypeEnum,
  ProfessionalApplicationStatusEnum,
  ReviewProData,
} from '@platform/client/schemas';
import { useState } from 'react';
import { SingleProAppStatusModal } from './UpdateProApplicationStatusModal';
import { UpdateCohortModal } from './UpdateCohortModal';
import { DisableUserModal } from './DisableUserModal';
import { MatchesTabContent } from './MatchesTabContent';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { MarkInactiveModal } from './MarkInactiveModal';
import { ProEmailTabContent } from './EmailTabContent';

export const ProfessionalDetail = () => {
  const { professionalId } = useParams<{ professionalId: string }>();
  const [resetUserAppModalOpen, setResetUserAppModalOpen] = useState(false);
  const [updateAppStatusModalOpen, setUpdateAppStatusModalOpen] = useState(false);
  const [updateCohortModalOpen, setUpdateCohortModalOpen] = useState(false);
  const [disableUserModalOpen, setDisableUserModalOpen] = useState(false);
  const [markInactiveModalOpen, setMarkInactiveModalOpen] = useState(false);
  const {
    data: proData,
    isSuccess,
    isError,
    isLoading,
  } = AdminProfessionals.useGetProfessionalReviewData(parseInt(professionalId));
  const cohortParticipationActions: DetailPageAction[] = [
    {
      text: 'Update cohort',
      onPress: () => setUpdateCohortModalOpen(true),
      type: 'button',
    },
  ];
  // Currently we do not support non Inactive 'non corporate' professionals
  // That UI is scheduled for release June 2026 (see comments in DashboardHomePanel.tsx)
  if (!proData?.inactiveDate && proData?.corporateClient) {
    cohortParticipationActions.push({
      text: 'Mark inactive',
      onPress: () => setMarkInactiveModalOpen(true),
      type: 'button',
    });
  }
  const pageActions: SectionedPageActionsProps[] = [
    {
      header: 'Cohort Participation',
      actions: cohortParticipationActions,
    },
  ];
  if (proData?.applicationId) {
    pageActions.push({
      header: 'Application',
      actions: [
        {
          text: 'Update application status',
          onPress: () => setUpdateAppStatusModalOpen(true),
          type: 'button',
        },
        {
          text: 'Reset application',
          onPress: () => setResetUserAppModalOpen(true),
          type: 'button',
        },
      ],
    });
  }
  if (!proData?.disabledDate) {
    pageActions.push({
      header: 'Account & Data',
      actions: [
        {
          text: 'Disable account',
          onPress: () => setDisableUserModalOpen(true),
          isDanger: true,
          type: 'button',
        },
      ],
    });
  }

  return (
    <>
      <AdminDetailPage
        isError={isError}
        isLoading={isLoading}
        isSuccess={isSuccess}
        title={proData?.name || 'MovingWorlds Professional'}
        alerts={getProAlerts(proData)}
        previewLink={`~/platform/directory/professionals/${professionalId}`}
        tabs={[
          {
            id: 'Profile',
            content: <ProfileTabContent proData={proData} />,
          },
          {
            id: 'Matches',
            content: <MatchesTabContent professionalId={parseInt(professionalId)} />,
          },
          {
            id: 'Emails',
            content: <ProEmailTabContent proId={parseInt(professionalId)} />,
          },
        ]}
        sectionedPageActions={pageActions}
      />
      {resetUserAppModalOpen && proData && (
        <ResetUserModal
          userId={proData.userId}
          currentAccountType={AccountTypeEnum.experteer}
          currentAppStatus={proData.applicationStatus}
          onClose={() => setResetUserAppModalOpen(false)}
        />
      )}
      {updateAppStatusModalOpen && proData && (
        <SingleProAppStatusModal
          onClose={() => setUpdateAppStatusModalOpen(false)}
          applicationId={proData.applicationId as number}
          status={proData.applicationStatus as ProfessionalApplicationStatusEnum}
          userId={proData.userId}
          isSapPro={proData.corporateClient === 'SAP'}
          queryKeyToInvalidate={AdminProfessionals.getGetProfessionalReviewDataQueryKey(
            proData.userId,
          )}
        />
      )}
      {updateCohortModalOpen && proData && (
        <UpdateCohortModal
          userId={proData.userId}
          onClose={() => setUpdateCohortModalOpen(false)}
          currentCohort={proData.cohortName}
          currentPartner={proData.corporateClient || ''}
          proName={proData.name}
          queryKeyToInvalidate={AdminProfessionals.getGetProfessionalReviewDataQueryKey(
            proData.userId,
          )}
        />
      )}
      <DisableUserModal
        userId={proData?.userId as number}
        onClose={() => setDisableUserModalOpen(false)}
        isOpen={disableUserModalOpen}
      />
      <MarkInactiveModal
        userId={proData?.userId as number}
        onClose={() => setMarkInactiveModalOpen(false)}
        isOpen={markInactiveModalOpen}
        userCurrentCohort={proData?.cohortName}
      />
    </>
  );
};

const getProAlerts = (proData?: ReviewProData): AlertProps[] => {
  const alerts: AlertProps[] = [];
  if (proData?.disabledDate) {
    alerts.push({
      variant: 'error',
      children: (
        <p className='font-bold text-red-900'>
          {`Account disabled on ${formatDateStr(proData.disabledDate)}.`}
        </p>
      ),
    });
  }
  if (proData?.inactiveDate) {
    alerts.push({
      variant: 'error',
      children: (
        <p className='font-bold text-red-900'>{` This user was marked inactive on ${formatDateStr(proData.inactiveDate)}.`}</p>
      ),
    });
  }
  return alerts;
};
